import { ReactNode, useEffect } from "react";
import { motion, useInView, animate } from "framer-motion";
import { useRef, useState } from "react";

export const EASE = [0.22, 1, 0.36, 1] as const;

export function Reveal({ children, delay = 0, y = 36, className = "" }: { children: ReactNode; delay?: number; y?: number; className?: string }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.9, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

export function MaskLines({ lines, className = "", lineClassName = "", delay = 0, onLoad = false }: { lines: ReactNode[]; className?: string; lineClassName?: string; delay?: number; onLoad?: boolean }) {
  const parent = { hidden: {}, show: { transition: { staggerChildren: 0.13, delayChildren: delay } } };
  const child = { hidden: { y: "108%", rotate: 2 }, show: { y: "0%", rotate: 0, transition: { duration: 1.15, ease: EASE } } };
  const trigger = onLoad ? { animate: "show" } : { whileInView: "show", viewport: { once: true, amount: 0.15 } };
  return (
    <motion.span className={`block ${className}`} initial="hidden" variants={parent} {...trigger}>
      {lines.map((l, i) => (
        <span key={i} className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
          <motion.span className={`block origin-left ${lineClassName}`} variants={child}>
            {l}
          </motion.span>
        </span>
      ))}
    </motion.span>
  );
}

export function CountUp({ to, decimals = 1, className = "", testId }: { to: number; decimals?: number; className?: string; testId?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });
  const [val, setVal] = useState(to);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, { duration: 2.2, ease: EASE, onUpdate: (v) => setVal(v) });
    return () => controls.stop();
  }, [inView, to]);
  return (
    <span ref={ref} className={className} data-testid={testId}>
      {val.toFixed(decimals)}
    </span>
  );
}
